import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import client from '../../api/client';
import GameHeader from './GameHeader';
import FullOddsComparison from './FullOddsComparison';
import LineMovementChart from './LineMovementChart';
import LineAnalysis from './LineAnalysis';
import KeyFactorsPanel from './KeyFactorsPanel';
import AiGamePreview from './AiGamePreview';
import EventAlerts from './EventAlerts';
import InjuryImpact from './InjuryImpact';
import WeatherImpact from './WeatherImpact';
import GameChat from './GameChat';

function inferSport(eventId) {
  if (eventId.startsWith('nfl')) return 'americanfootball_nfl';
  if (eventId.startsWith('nba')) return 'basketball_nba';
  if (eventId.startsWith('mlb')) return 'baseball_mlb';
  if (eventId.startsWith('mma')) return 'mma_mixed_martial_arts';
  if (eventId.startsWith('box')) return 'boxing_boxing';
  if (eventId.startsWith('golf')) return 'golf';
  return 'americanfootball_nfl';
}

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL', americanfootball_ncaaf: 'NCAAF',
  basketball_nba: 'NBA', basketball_ncaab: 'NCAAB',
  baseball_mlb: 'MLB', icehockey_nhl: 'NHL', soccer_usa_mls: 'MLS',
  mma_mixed_martial_arts: 'MMA', boxing_boxing: 'Boxing', golf: 'Golf',
};

function Breadcrumbs({ sport, event }) {
  const sportLabel = SPORT_LABELS[sport] || sport;
  const matchup = event ? `${event.away_team} vs ${event.home_team}` : null;

  return (
    <nav className="flex items-center gap-0 text-[14px] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>
      <Link to="/" className="text-[#6B7280] dark:text-[#8892a8] hover:text-[#005BB3] dark:hover:text-[#3b82f6] transition-colors duration-150">Odds Board</Link>
      <span className="text-[#9CA3AF] dark:text-[#505a6e] mx-2">›</span>
      <Link to={`/?sport=${sport}`} className="text-[#6B7280] dark:text-[#8892a8] hover:text-[#005BB3] dark:hover:text-[#3b82f6] transition-colors duration-150">{sportLabel}</Link>
      {matchup && (
        <>
          <span className="text-[#9CA3AF] dark:text-[#505a6e] mx-2">›</span>
          <span className="text-[#111827] dark:text-[#e2e8f0] font-semibold">{matchup}</span>
        </>
      )}
    </nav>
  );
}

function Skeleton({ className = '' }) {
  return <div className={`rounded bg-[#f1f5f9] dark:bg-[#151a25] skeleton ${className}`} />;
}

export default function GameDetail() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const sport = searchParams.get('sport') || inferSport(eventId);

  const [event, setEvent] = useState(null);
  const [lineData, setLineData] = useState(null);
  const [context, setContext] = useState(null);
  const [scores, setScores] = useState(null);
  const [error, setError] = useState(null);

  const [loadingEvent, setLoadingEvent] = useState(true);
  const [loadingLine, setLoadingLine] = useState(true);
  const [loadingContext, setLoadingContext] = useState(true);
  const [loadingScores, setLoadingScores] = useState(true);

  function fetchContext(homeTeam, awayTeam, gameTime) {
    const params = new URLSearchParams({ sport });
    if (homeTeam) params.set('home_team', homeTeam);
    if (awayTeam) params.set('away_team', awayTeam);
    if (gameTime) params.set('game_time', gameTime);
    const url = `/api/context/${eventId}?${params.toString()}`;
    client.get(url)
      .then((r) => setContext(r.data))
      .catch(() => setContext(null))
      .finally(() => setLoadingContext(false));
  }

  function fetchAll() {
    setError(null);
    setLoadingEvent(true);
    setLoadingLine(true);
    setLoadingContext(true);
    setLoadingScores(true);

    client.get(`/api/odds/${sport}/${eventId}`)
      .then((r) => { setEvent(r.data); setLoadingEvent(false); fetchContext(r.data?.home_team, r.data?.away_team, r.data?.commence_time); })
      .catch((e) => { setEvent(null); setError(e.message); setLoadingEvent(false); fetchContext(null, null, null); });

    client.get(`/api/line-movement/${eventId}`)
      .then((r) => setLineData(r.data))
      .catch(() => setLineData([]))
      .finally(() => setLoadingLine(false));

    client.get(`/api/scores/${sport}`)
      .then((r) => { const match = r.data.find((s) => s.id === eventId); setScores(match || null); })
      .catch(() => setScores(null))
      .finally(() => setLoadingScores(false));
  }

  useEffect(() => { fetchAll(); }, [eventId, sport]);

  const hasLineData = lineData && lineData.length > 0;

  return (
    <div className="px-5 py-4 space-y-3">
      <Breadcrumbs sport={sport} event={event} />

      {error && !event && !loadingEvent && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 text-center">
          <p className="text-[#ef4444] text-[11px]">Failed to load event.</p>
          <button onClick={fetchAll} className="text-[10px] text-[#ef4444] underline mt-1">Retry</button>
        </div>
      )}

      <div className="max-w-[1100px] mx-auto">
        {loadingEvent ? <Skeleton className="h-20" /> : event ? <GameHeader event={event} scores={scores} context={context} /> : !error ? (
          <div className="rounded border border-[#fecaca] dark:border-[#7f1d1d] bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] px-3 py-2"><p className="text-[#ef4444] text-[11px]">Event not found.</p></div>
        ) : null}
      </div>

      {loadingEvent ? <Skeleton className="h-48" /> : event ? <FullOddsComparison event={event} /> : null}

      {loadingLine ? <Skeleton className="h-56" /> : <LineMovementChart data={lineData || []} />}

      {/* Two-column layout — full width */}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Left column — Context & data */}
        <div className="space-y-3 min-w-0 w-full lg:w-1/2">
          {loadingContext ? <Skeleton className="h-36" /> : <KeyFactorsPanel context={context} sport={sport} />}
          {!loadingContext && <InjuryImpact eventId={eventId} sport={sport} hasInjuries={context?.injuries?.length > 0} homeTeam={context?.home_team || event?.home_team} awayTeam={context?.away_team || event?.away_team} />}
          {!loadingContext && <WeatherImpact eventId={eventId} sport={sport} hasWeather={!!context?.weather} homeTeam={context?.home_team || event?.home_team} />}
          <EventAlerts eventId={eventId} />
        </div>

        {/* Right column — AI analysis */}
        <div className="space-y-3 min-w-0 w-full lg:w-1/2">
          {!loadingLine && <LineAnalysis eventId={eventId} sport={sport} hasLineData={hasLineData} />}
          <AiGamePreview eventId={eventId} sport={sport} />
          <GameChat eventId={eventId} sport={sport} homeTeam={event?.home_team} awayTeam={event?.away_team} />
        </div>
      </div>
    </div>
  );
}
